<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'CreatePassThroughInvoice',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    invoice: {
      type: Object,
      required: true,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const formId = ref(null);
  const item = ref(null);
  const labelAlign = ref('right');
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'md',
  }));
  const b_7789867021953041_dataSource = computed(() => ({
    name: 'suggest_company',
  }));

  const $data = computed(() => ({
    formId: formId.value,
    item: item.value,
    labelAlign: labelAlign.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_7789867021953041_dataSource: b_7789867021953041_dataSource.value,
  }));

  const $$load_formId = async () => {
    return $fn.value.randomElementId();
  };
  const $$load_item = async () => {
    return {
      invoice_type: 'pass_through',
      invoice_id: props.invoice.id,
      client: null,
      ratio: 1,
    };
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'formId' },
      { name: 'item' },
      { name: 'labelAlign' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_7789867021953041_dataSource', nodeId: '7789867021953041' },
    ],
    loaders: { formId: $$load_formId, item: $$load_item },
    ux: {},
    dataRefs: {
      formId,
      item,
      labelAlign,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_7789867021953041_dataSource,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.pgettext('Invoice', 'Create Pass-through Invoice'),
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.invoice,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('invoice');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_d6mxo7vt5_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_navigate_f474ix2qo_getActionArgs = async (
    $event
  ) => {
    return {
      location: {
        name: 'app.invoice',
        params: {
          id: $event.data.response.data.id,
        },
      },
    };
  };
  const act_1040167445267876_request_compno31b_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'create_invoice',
      data: item.value,
    };
  };
  const act_2434514588044777_closeModal_3nivlxysv_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        close: true,
      },
    };
  };

  const act_7315092382398562_closeModal_d6mxo7vt5 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_d6mxo7vt5_getActionArgs,
    });
  const inst_evh_7315092382398562_close_98c1zso9o =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_d6mxo7vt5,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_98c1zso9o],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_0ylj18ldt =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_31q3lg50m =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_0ylj18ldt,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_31q3lg50m],
    event: 'reload',
  });
  const act_8564662037462133_closeModal_r45hm0gdh =
    new actionTypes.CloseModalAction({});
  const inst_evh_8564662037462133_close_g7sk80c9l =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeModal_r45hm0gdh,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_g7sk80c9l],
    event: 'close',
  });
  const act_1040167445267876_navigate_f474ix2qo =
    new actionTypes.NavigateAction({
      actionArgs: act_1040167445267876_navigate_f474ix2qo_getActionArgs,
    });
  const inst_evh_1040167445267876_success_gphww99xp =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_navigate_f474ix2qo,
      event: 'success',
      displayName: 'navigate',
    });
  const act_1040167445267876_closeComponent_xjfxvatxx =
    new actionTypes.CloseComponentAction({});
  const inst_evh_1040167445267876_success_2gfgo4b50 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeComponent_xjfxvatxx,
      event: 'success',
      displayName: 'closeComponent',
    });
  const act_1040167445267876_request_compno31b = new actionTypes.RequestAction({
    actionArgs: act_1040167445267876_request_compno31b_getActionArgs,
    displayName: 'submit',
    events: [
      inst_evh_1040167445267876_success_gphww99xp,
      inst_evh_1040167445267876_success_2gfgo4b50,
    ],
  });
  const inst_evh_1040167445267876_submit_gjef2gxbv =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_request_compno31b,
      event: 'submit',
      displayName: 'submit',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_gjef2gxbv],
    event: 'submit',
  });
  const act_2434514588044777_closeModal_3nivlxysv =
    new actionTypes.CloseModalAction({
      actionArgs: act_2434514588044777_closeModal_3nivlxysv_getActionArgs,
    });
  const inst_evh_2434514588044777_clickPreventStop_8v8sed2r4 =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeModal_3nivlxysv,
      event: 'click.prevent.stop',
      displayName: 'closeModal',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_8v8sed2r4],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_98c1zso9o,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_31q3lg50m,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_g7sk80c9l,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_gphww99xp,
    inst_evh_1040167445267876_success_2gfgo4b50,
    inst_evh_1040167445267876_submit_gjef2gxbv,
    submit: inst_evh_1040167445267876_submit_gjef2gxbv,
    inst_evh_1040167445267876_submit,
    inst_evh_2434514588044777_clickPreventStop_8v8sed2r4,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_98c1zso9o = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_98c1zso9o.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_31q3lg50m = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_31q3lg50m.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_g7sk80c9l = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_g7sk80c9l.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_gphww99xp = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_gphww99xp.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_2gfgo4b50 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_2gfgo4b50.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_gjef2gxbv = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_gjef2gxbv.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_8v8sed2r4 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_8v8sed2r4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="CreatePassThroughInvoice"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :id="formId"
        name="invoice"
        autofocus="first"
        v-if="loaded"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpFormGroup
          name="client"
          :label="pgettext('Invoice', 'Client')"
          :labelAlign="labelAlign"
          :label-cols-md="3"
          :content-cols-md="9"
          :helpTooltip="
            pgettext(
              'CreatePassThroughInvoice|Tooltip',
              'Specify the client to be re-invoiced.'
            )
          "
          required
        >
          <ItpSelect2
            name="client"
            v-model="item.client"
            :value="item.client"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.client =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :dataSource="b_7789867021953041_dataSource"
            valueField="id"
            textField="name"
            bind-object
            optionTemplate="<UcSuggestCompanyOption :option=option></UcSuggestCompanyOption>"
            use-option-template-for-selected-option
            required
          >
          </ItpSelect2>
        </ItpFormGroup>
        <ItpFormGroup
          name="ratio"
          :label="pgettext('CreatePassThroughInvoice', 'Multiplier')"
          :labelAlign="labelAlign"
          :label-cols-md="3"
          :content-cols-md="3"
          :helpTooltip="
            pgettext(
              'CreatePassThroughInvoice|Tooltip',
              'Line items will be generated with the given multiplier.'
            )
          "
          required
        >
          <ItpFormInput
            type="number"
            name="ratio"
            v-model="item.ratio"
            :value="item.ratio"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.ratio =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
          >
          </ItpFormInput>
        </ItpFormGroup>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :form="forms.invoice"
          :text="pgettext('CreateInvoice|Submit', 'Create invoice')"
          type="submit"
          variant="primary"
          :spinning="actions.submit && actions.submit.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          :disabled="actions.submit.isRunning"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
